import find from 'lodash.find';
import moment from 'moment';
import { SERVICE_TYPE } from '~../__queries__/globalTypes';
import { TranslatedString, i18nProps } from '~Types';
import { lowerCase, startCase } from 'lodash';
import { CategoryTotals } from '@baemingo/report-events/dist/types';
import { DateTime } from 'luxon';
import { getAppliedMenus_getAppliedMenus_results } from '~Components/MenuFetchingComponent/__queries__';
import { PrinterStatusResponse } from '~Hooks';

export function formatTime(minutes: number) {
  let resultHours: number | null = null;
  let resultMinutes: number | null = null;
  if (minutes > 59) {
    resultHours = parseInt((minutes / 60).toString(), 10);
    resultMinutes = Math.abs(minutes - resultHours * 60);
    return `${resultHours} h ${resultMinutes} min`;
  }
  return `${minutes}`;
}
export function addMinutesToDate(date: Date, minutes: number) {
  date.setMinutes(date.getMinutes() + minutes);
  return date;
}

export function findElement(collection: any, searchElement: string) {
  return find(collection, searchElement);
}

export function calculateSum(collection: Record<string, any>, prop: string) {
  const initialValue = 0;
  return collection.reduce((sum: any, obj: any) => {
    const element = findElement(obj, prop);
    const value = !element ? 0 : element[prop];
    return sum + value;
  }, initialValue);
}

export function filterCollectionByPropertyValue(
  collection: any[],
  prop: string,
  type: string,
  value: string,
) {
  if (!collection) {
    return [];
  }

  if (type !== undefined) {
    return collection.filter((item) => {
      return item[prop] === value && item.type === type.toUpperCase();
    });
  }
  return collection.filter((item) => {
    return item[prop] === value;
  });
}

export function getEstimatedMinutes(estimatedFinishTime: string) {
  const estimatedDate = new Date(estimatedFinishTime);
  const nowDate = new Date();
  const timeDifference = estimatedDate.getTime() - nowDate.getTime();

  const estimatedMinutes = Math.ceil(timeDifference / 1000 / 60);

  return estimatedMinutes;
}

export function getTotalMinutesTime(createdAt: string, estimatedFinishTime: string) {
  const createdAtDate = new Date(createdAt);
  const estimatedDate = new Date(estimatedFinishTime);
  const timeDifference = estimatedDate.getTime() - createdAtDate.getTime();

  const totalMinutes = Math.ceil(timeDifference / 1000 / 60);
  return totalMinutes;
}

export function getLanguageValue(
  dataName: string,
  lang: {
    [key: string]: string;
  },
  locale: string,
) {
  if (lang) {
    return lang[locale] ? lang[locale] : dataName;
  }
  return dataName;
}

export function getFallbackTranslation(
  translatedString: {
    [key: string]: string;
  },
  language: string,
) {
  if (translatedString) {
    if (language) {
      if (translatedString[language]) {
        return translatedString[language];
      }
    }
    const keys = Object.keys(translatedString);
    for (let i = 0; i < keys.length; i += 1) {
      if (translatedString[keys[i]] && keys[i] !== 'id' && keys[i] !== '__typename') {
        return translatedString[keys[i]];
      }
    }
  }
  return '';
}

export function checkScreen(pathname: string, value: string) {
  return pathname?.indexOf(value) !== -1;
}

/**
 * Takes a moment or date string & formats it to time, & date if date is different from today
 */
export function formatTimeDate(time: moment.Moment | string) {
  const momentTime = moment(time).local();
  const isToday = momentTime.isSameOrBefore(moment(), 'day');
  return momentTime.format(isToday ? 'HH:mm' : 'DD/MM HH:mm');
}

export const formatOrderType = (
  type: SERVICE_TYPE,
  i18n: (obj: string | TranslatedString | null) => string,
) => {
  switch (type) {
    case 'TAKE_AWAY':
      return i18n('OrderType.TakeAway');
    case 'DELIVERY':
      return i18n('OrderType.Delivery');
    case 'EAT_HERE':
      return i18n('OrderType.EatHere');
    case 'INSTANT_BUY':
      return i18n('OrderType.InstantBuy');
    case 'TABLE_SERVICE':
      return i18n('OrderType.TableService');
    case 'LOCAL_DELIVERY':
      return i18n('OrderType.LocalDelivery');
    case 'SHIPPED':
      return i18n('OrderType.Shipped');
    case 'BOOKING':
      return i18n('OrderType.Booking');
    case 'POS':
      return i18n('OrderType.POS');
    default:
      return startCase(lowerCase(type));
  }
};

interface Category {
  name: string;
  amount: number;
  count: number;
  subCategory: { [key: string]: Category }; // Recursive type reference for nested subcategories
}

export type AggregatedCategoryMap = { [key: string]: Category };

export const aggregateCategorySales = (
  categories: {
    id: string;
    name?: string;
    nameLang?: TranslatedString;
    parentId?: string | undefined;
  }[],
  categoryTotals: CategoryTotals,
  i18n: i18nProps,
) => {
  const categoryMap: AggregatedCategoryMap = categories.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.id]: {
        ...cur,
        name: cur.nameLang ? i18n(cur.nameLang) : cur.name,
        amount: 0,
        count: 0,
        subCategory: {},
      },
    }),
    {},
  );

  Object.entries(categoryTotals).forEach(([id, totals]) => {
    if (categoryMap[id]) {
      categoryMap[id].amount = totals.amount;
      categoryMap[id].count = totals.count;
    }
  });

  function buildCategoryStructure(id: string) {
    const category = categoryMap[id];
    if (!category) return null;

    if (categoryTotals[id]) {
      category.subCategory[id + `(${i18n('Report.AggregatedCategories.Direct')})`] = {
        name: category.name + ` (${i18n('Report.AggregatedCategories.Direct')})`,
        amount: categoryTotals[id].amount,
        count: categoryTotals[id].count,
        subCategory: {},
      };
    }

    categories
      .filter((cat) => cat.parentId === id)
      .sort((a, b) =>
        i18n(a.nameLang || a.name || '').localeCompare(i18n(b.nameLang || b.name || '')),
      )
      .forEach((subCat) => {
        const subCategoryStructure = buildCategoryStructure(subCat.id);
        if (subCategoryStructure) {
          category.subCategory[subCat.id] = subCategoryStructure;
          category.amount += subCategoryStructure.amount;
          category.count += subCategoryStructure.count;
        }
      });

    return category;
  }

  const topLevelCategories: AggregatedCategoryMap = {};
  categories
    .filter((cat) => !cat.parentId)
    .sort((a, b) =>
      i18n(a.nameLang || a.name || '').localeCompare(i18n(b.nameLang || b.name || '')),
    )
    .forEach((cat) => {
      topLevelCategories[cat.id] = buildCategoryStructure(cat.id) ?? {
        name: 'unknown',
        amount: 0,
        count: 0,
        subCategory: {},
      };
    });

  return topLevelCategories;
};

export const findMenuForCurrentTime = (menus: getAppliedMenus_getAppliedMenus_results[]) => {
  const now = DateTime.local(); // Get current local date and time
  const currentDayOfWeek = now.toFormat('cccc').toUpperCase(); // Get current day of the week in uppercase

  return [...menus].find((m) => {
    if (!m.availabilities || m.availabilities.length === 0) {
      return false;
    }

    // Iterate over each availability entry
    return m.availabilities.some((availability: any) => {
      const { dayOfWeek, timeRange } = availability;

      // Check if the current day matches the availability's day of availability
      if (!dayOfWeek.includes(currentDayOfWeek)) {
        return false;
      }

      // Check if the current time is within the availability's time range
      const startTime = DateTime.fromFormat(timeRange.start, 'HH:mm');
      let endTime;

      if (timeRange.end === '00:00') {
        // We cannot compare 00:00 directly since it is the start of the day, so we need to convert it to 23:59:59:999
        endTime = DateTime.fromFormat('23:59:59:999', 'HH:mm:ss:SSS');
      } else {
        endTime = DateTime.fromFormat(timeRange.end, 'HH:mm');
      }

      // Check if the current time is between start and end time
      return now >= startTime && now < endTime;
    });
  });
};

export const formatPrinterStatus = (status: PrinterStatusResponse, i18n: i18nProps) => {
  if (status.drawerIsOpen) {
    return i18n('Printer.Status.DrawerOpen');
  }

  if (status.coverIsOpen) {
    return i18n('Printer.Status.CoverOpen');
  }

  if (status.paperEmpty) {
    return i18n('Printer.Status.NoPaper');
  }

  if (status.paperNearEmpty) {
    return i18n('Printer.Status.PaperNearEmpty');
  }

  if (!status.isOffline || status.isResponsive === true) {
    return i18n('Printer.Status.Online');
  }

  return i18n('Printer.Status.Offline');
};

export const formatPrinterError = (error: string | undefined, i18n: i18nProps) => {
  switch (error) {
    // Could not connect to the printer
    case 'Please connect to the printer before sending commands. If connected, remove the printer and reconnect it.': // Star printer (@popmenu/star-print), port == nil
    case 'Printer is offline (BeginCheckedBlock)': // Star printer (@popmenu/star-print)
    case 'Write port timed out (PortException)': // Star printer (@popmenu/star-print)
    case 'Error: Failed to connect to printer': // Epson
    case 'Printer is offline (EndCheckedBlock)': // Star printer (@popmenu/star-print)
    case 'Network request failed':
    case 'Could not connect to printer - timeout after 15 seconds': // Order-manager-app - StarPrnt
    case 'Could not connect to printer - timeout after 8 seconds':
    case 'Could not connect to printer':
    case 'generic_error': // Order-manager-app - StarIO
    case 'Write failed.': // Star
    case 'Network unavailable.': // Star
    case 'Device has error.': // Star
    case 'Print timed out.': // Star
    case 'Device not found.': // StarIO10NotFoundError
    case 'EX_ENPC_TIMEOUT': // Epson printer (@baemingo/epson-printer-sdk)
      return i18n('Printer.Error.Connection');

    // Bad port
    case 'EX_BADPORT': // Epson printer (@baemingo/epson-printer-sdk)
      return i18n('Printer.Error.BadPort');

    case 'Open failed.': // Star
      return i18n('Printer.Error.OpenFailed');

    // Cover is open
    case 'cover_open': // Order-manager-app - StarIO
    case 'EPTR_COVER_OPEN': // Epson printer (@baemingo/epson-printer-sdk)
      return i18n('Printer.Error.CoverOpen');

    // Paper is empty
    case 'EPTR_REC_EMPTY': // Epson printer (@baemingo/epson-printer-sdk)
    case 'paper_empty': // Order-manager-app - StarIO
      return i18n('Printer.Error.NoPaper');

    // Cutter error
    case 'EPTR_CUTTER': // Epson printer (@baemingo/epson-printer-sdk)
      return i18n('Printer.Error.Cutter');

    // Mechanical
    case 'EPTR_MECHANICAL':
      return i18n('Printer.Error.Mechanical');

    default:
      return i18n('Printer.Error.Unknown');
  }
};

// If the message already contains the courseTypeLabel, don't add it again.
// Example message is "Wait MC" and courseTypeLabel is "MC", we don't want the message to be "Wait MC MC"
// If the message is "Wait" and courseTypeLabel is "MC", we want the message to be "Wait MC"
export const formatTicketMessage = (message: string, courseLabel?: string) => {
  if (!courseLabel) {
    return message;
  }

  const pattern = new RegExp(`\\b${courseLabel}\\b`);
  return pattern.test(message) ? message : `${message} ${courseLabel}`;
};

export const checkBrowserAllowClick = (allowBrowserClick: boolean) => {
  const isTizen =
    typeof window !== 'undefined' && window.navigator.userAgent.toLowerCase().includes('tizen');

  const isUsingBrowser = !window.ReactNativeWebView;

  const isStorybook = !!process.env.STORYBOOK_MODE;

  return isTizen || isStorybook || (isUsingBrowser && allowBrowserClick);
};
